import { useState, useEffect } from 'react';
import { api, useAuthStore } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Loader2, 
  Calendar,
  FileText,
  RefreshCw,
  CheckCircle,
  XCircle,
  Users,
  AlertTriangle,
  X,
  Copy,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { cn } from '../lib/utils';

export const LeaveTypesManagement = () => {
  const { t, language } = useTranslation();
  const { user } = useAuthStore();
  
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, leaveType: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, leaveType: null });
  const [usersDialog, setUsersDialog] = useState({ open: false, leaveType: null, users: [], loading: false });
  const [applyToAllDialog, setApplyToAllDialog] = useState({ open: false, leaveType: null, oldDays: 0, newDays: 0 });
  const [duplicatesDialog, setDuplicatesDialog] = useState({ open: false, loading: false, duplicates: [] });
  const [orphansDialog, setOrphansDialog] = useState({ open: false, loading: false, orphans: [] });
  
  // Form state
  const [formData, setFormData] = useState({
    code: '',
    name_tr: '',
    name_en: '',
    name_nl: '',
    name_fr: '',
    default_limit_days: 0,
    requires_document: false,
    is_active: true,
    color: '#3B82F6',
    admin_only: false
  });

  // Preset colors
  const presetColors = [
    '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', 
    '#F59E0B', '#EF4444', '#06B6D4', '#84CC16',
    '#6366F1', '#F97316', '#14B8A6', '#A855F7'
  ];

  useEffect(() => {
    fetchLeaveTypes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLeaveTypes = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/leave-types');
      if (response.data.success) {
        setLeaveTypes(response.data.data || []);
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name_tr: '',
      name_en: '',
      name_nl: '',
      name_fr: '',
      default_limit_days: 0,
      requires_document: false,
      is_active: true,
      color: '#3B82F6',
      admin_only: false
    });
  };

  const openCreateDialog = () => {
    resetForm();
    setCreateDialog(true);
  };

  const openEditDialog = (lt) => {
    setFormData({
      code: lt.code,
      name_tr: lt.name_i18n?.tr || '',
      name_en: lt.name_i18n?.en || '',
      name_nl: lt.name_i18n?.nl || '',
      name_fr: lt.name_i18n?.fr || '',
      default_limit_days: lt.default_limit_days || 0,
      requires_document: lt.requires_document || false,
      is_active: lt.is_active !== false,
      color: lt.color || '#3B82F6',
      admin_only: lt.admin_only || false
    });
    setEditDialog({ open: true, leaveType: lt });
  };

  const handleCreate = async () => {
    if (!formData.code || !formData.name_tr) {
      toast.error(t('leaveTypes.codeAndNameRequired') || 'Kod ve Türkçe isim zorunludur');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        code: formData.code.toUpperCase().replace(/\s+/g, '_'),
        name_i18n: {
          tr: formData.name_tr,
          en: formData.name_en || formData.name_tr,
          nl: formData.name_nl || formData.name_tr,
          fr: formData.name_fr || formData.name_tr
        },
        default_limit_days: parseInt(formData.default_limit_days) || 0,
        requires_document: formData.requires_document,
        is_active: formData.is_active,
        admin_only: formData.admin_only,
        color: formData.color
      };

      const response = await api.post('/admin/leave-types', payload);
      if (response.data.success) {
        toast.success(t('leaveTypes.created') || 'İzin türü oluşturuldu');
        setCreateDialog(false);
        fetchLeaveTypes();
      }
    } catch (error) {
      const detail = error.response?.data?.detail || t('common.error');
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (applyToAll = false) => {
    setSaving(true);
    try {
      const payload = {
        name_i18n: {
          tr: formData.name_tr,
          en: formData.name_en || formData.name_tr,
          nl: formData.name_nl || formData.name_tr,
          fr: formData.name_fr || formData.name_tr
        },
        default_limit_days: parseInt(formData.default_limit_days) || 0,
        requires_document: formData.requires_document,
        is_active: formData.is_active,
        color: formData.color,
        admin_only: formData.admin_only,
        apply_to_all_users: applyToAll
      };

      const response = await api.put(`/admin/leave-types/${editDialog.leaveType.code}`, payload);
      if (response.data.success) {
        if (applyToAll && response.data.data?.updated_count) {
          toast.success(`İzin türü güncellendi ve ${response.data.data.updated_count} kullanıcının bakiyesi güncellendi`);
        } else {
          toast.success(t('leaveTypes.updated') || 'İzin türü güncellendi');
        }
        setEditDialog({ open: false, leaveType: null });
        setApplyToAllDialog({ open: false, leaveType: null, oldDays: 0, newDays: 0 });
        fetchLeaveTypes();
      }
    } catch (error) {
      const detail = error.response?.data?.detail || t('common.error');
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  // Check if days changed and ask to apply to all
  const checkAndUpdate = () => {
    const oldDays = editDialog.leaveType?.default_limit_days || 0;
    const newDays = parseInt(formData.default_limit_days) || 0;
    
    if (oldDays !== newDays) {
      setApplyToAllDialog({ 
        open: true, 
        leaveType: editDialog.leaveType, 
        oldDays, 
        newDays 
      });
    } else {
      handleUpdate(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const response = await api.delete(`/admin/leave-types/${deleteDialog.leaveType.code}`);
      if (response.data.success) {
        toast.success(t('leaveTypes.deleted') || 'İzin türü silindi');
        setDeleteDialog({ open: false, leaveType: null });
        fetchLeaveTypes();
      }
    } catch (error) {
      const detail = error.response?.data?.detail || t('common.error');
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (lt) => {
    try {
      await api.put(`/admin/leave-types/${lt.code}`, { is_active: !lt.is_active });
      toast.success(lt.is_active ? (t('leaveTypes.deactivated') || 'Pasif yapıldı') : (t('leaveTypes.activated') || 'Aktif yapıldı'));
      fetchLeaveTypes();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const getLocalizedName = (lt) => {
    return lt.name_i18n?.[language] || lt.name_i18n?.tr || lt.code;
  };

  // Fetch users who have this leave type
  const fetchLeaveTypeUsers = async (leaveType) => {
    setUsersDialog({ open: true, leaveType, users: [], loading: true });
    try {
      const response = await api.get(`/admin/leave-types/${leaveType.code}/users`);
      if (response.data.success) {
        setUsersDialog(prev => ({ 
          ...prev, 
          users: response.data.data.users || [],
          totalBalances: response.data.data.total_balances || 0,
          totalRequests: response.data.data.total_requests || 0,
          loading: false 
        }));
      }
    } catch (error) {
      toast.error(t('common.error'));
      setUsersDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Remove balance from a specific user
  const removeUserBalance = async (userId, leaveTypeCode, userName, balanceId) => {
    if (!confirm(`${userName} kullanıcısından "${getLocalizedName(usersDialog.leaveType)}" izin bakiyesini silmek istediğinize emin misiniz?`)) {
      return;
    }
    
    try {
      const response = await api.delete(`/admin/users/${userId}/balances/${leaveTypeCode}${balanceId ? `?balance_id=${balanceId}` : ''}`);
      if (response.data.success) {
        toast.success(`${userName} kullanıcısından izin bakiyesi kaldırıldı`);
        fetchLeaveTypeUsers(usersDialog.leaveType);
        fetchLeaveTypes();
      }
    } catch (error) {
      const detail = error.response?.data?.detail || t('common.error');
      toast.error(detail);
    }
  };

  // Fetch duplicates
  const fetchDuplicates = async () => {
    setDuplicatesDialog({ open: true, loading: true, duplicates: [] });
    try {
      const response = await api.get('/admin/leave-balances/duplicates');
      if (response.data.success) {
        setDuplicatesDialog(prev => ({ 
          ...prev, 
          duplicates: response.data.data || [],
          loading: false 
        }));
      }
    } catch (error) {
      toast.error(t('common.error'));
      setDuplicatesDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Delete a specific duplicate balance
  const deleteDuplicateBalance = async (balanceId, userName, leaveTypeName) => {
    try {
      const response = await api.delete(`/admin/leave-balances/${balanceId}`);
      if (response.data.success) {
        toast.success(`Mükerrer bakiye silindi: ${userName} - ${leaveTypeName}`);
        fetchDuplicates();
      }
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  // Fetch orphan balances (balances from old firms)
  const fetchOrphans = async () => {
    setOrphansDialog({ open: true, loading: true, orphans: [] });
    try {
      const response = await api.get('/admin/leave-balances/orphans');
      if (response.data.success) {
        setOrphansDialog({
          open: true,
          loading: false,
          orphans: response.data.data || [],
        });
      }
    } catch (error) {
      toast.error(t('common.error'));
      setOrphansDialog({ open: false, loading: false, orphans: [] });
    }
  };

  // Delete single orphan balance
  const deleteOrphanBalance = async (balanceId, userName, leaveTypeName) => {
    try {
      await api.delete(`/admin/leave-balances/${balanceId}`);
      toast.success(`Yetim bakiye silindi: ${userName} - ${leaveTypeName}`);
      // Refresh orphans list
      fetchOrphans();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  // Cleanup all orphan balances
  const cleanupAllOrphans = async () => {
    try {
      const response = await api.delete('/admin/leave-balances/orphans/cleanup');
      if (response.data.success) {
        toast.success(`${response.data.data.deleted_count} yetim bakiye temizlendi`);
        setOrphansDialog({ open: false, loading: false, orphans: [] });
      }
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  // Calculate remaining days
  const calculateRemaining = (balance) => {
    if (!balance) return 0;
    const entitled = balance.entitled_days || 0;
    const adjustments = (balance.adjustments || []).reduce((sum, adj) => sum + (adj.days || 0), 0);
    const used = balance.used_days || 0;
    return entitled + adjustments - used;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 
            className="text-2xl font-bold text-slate-900 dark:text-slate-100"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            {t('leaveTypes.title') || 'İzin Türleri Yönetimi'}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {t('leaveTypes.description') || 'Sistemdeki izin türlerini yönetin'}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={fetchOrphans}
            className="text-orange-600 border-orange-200 hover:bg-orange-50"
          >
            <AlertTriangle className="h-4 w-4 mr-2" />
            {t('leaveTypes.orphanBalance')}
          </Button>
          <Button
            variant="outline"
            onClick={fetchDuplicates}
            className="text-amber-600 border-amber-200 hover:bg-amber-50"
          >
            <Copy className="h-4 w-4 mr-2" />
            {t('leaveTypes.duplicateCheck')}
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={fetchLeaveTypes}
            disabled={loading}
          >
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          </Button>
          <Button onClick={openCreateDialog} className="gap-2">
            <Plus className="h-4 w-4" />
            {t('leaveTypes.add')}
          </Button>
        </div>
      </div>

      {/* Leave Types Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : leaveTypes.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar className="h-12 w-12 text-slate-300 mb-4" />
            <p className="text-slate-500 dark:text-slate-400">{t('leaveTypes.noData')}</p>
            <Button className="mt-4" onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              {t('leaveTypes.add')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {leaveTypes.map((lt) => (
            <Card key={lt.id} className={cn("border-l-4 transition-all hover:shadow-md", !lt.is_active && "opacity-60")} style={{ borderLeftColor: lt.color || '#6B7280' }}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: lt.color || '#6B7280' }}
                    />
                    <CardTitle className="text-base">{getLocalizedName(lt)}</CardTitle>
                  </div>
                  <Badge variant={lt.is_active ? "default" : "secondary"} className="text-xs">
                    {lt.is_active ? t('leaveTypes.active') : t('leaveTypes.inactive')}
                  </Badge>
                </div>
                <p className="text-xs text-slate-400 font-mono">{lt.code}</p>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between text-sm mb-3">
                  <span className="text-slate-500 dark:text-slate-400">{t('leaveTypes.defaultLabel')}:</span>
                  <span className="font-semibold">{lt.default_limit_days} {t('leaveTypes.days')}</span>
                </div>
                <div className="flex items-center justify-between text-sm mb-3">
                  <span className="text-slate-500 dark:text-slate-400">{t('leaveTypes.requiresDocument')}:</span>
                  {lt.requires_document ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <XCircle className="h-4 w-4 text-slate-300" />
                  )}
                </div>
                <div className="flex items-center justify-between text-sm mb-3">
                  <span className="text-slate-500 dark:text-slate-400">{t('leaveTypes.adminOnly')}:</span>
                  {lt.admin_only ? (
                    <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                      {t('leaveTypes.yes')}
                    </Badge>
                  ) : (
                    <XCircle className="h-4 w-4 text-slate-300" />
                  )}
                </div>
                
                <div className="flex items-center gap-1 pt-3 border-t border-slate-100 dark:border-slate-700">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEditDialog(lt)}
                    className="flex-1"
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    {t('leaveTypes.edit')}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => fetchLeaveTypeUsers(lt)}
                    className="flex-1"
                  >
                    <Users className="h-4 w-4 mr-1" />
                    {t('leaveTypes.viewUsers')}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => setDeleteDialog({ open: true, leaveType: lt })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-md max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>{t('leaveTypes.add')}</DialogTitle>
            <DialogDescription>{t('leaveTypes.createDescription')}</DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 py-2 pr-2">
            <div>
              <Label>{t('leaveTypes.code')} *</Label>
              <Input
                placeholder="ANNUAL_LEAVE"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase().replace(/\s+/g, '_') })}
                className="font-mono"
              />
              <p className="text-xs text-slate-400 mt-1">{t('leaveTypes.onlyUppercaseAndUnderscore')}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>🇹🇷 Türkçe *</Label>
                <Input placeholder="Yıllık İzin" value={formData.name_tr} onChange={(e) => setFormData({ ...formData, name_tr: e.target.value })} />
              </div>
              <div>
                <Label>🇬🇧 English</Label>
                <Input placeholder="Annual Leave" value={formData.name_en} onChange={(e) => setFormData({ ...formData, name_en: e.target.value })} />
              </div>
              <div>
                <Label>🇳🇱 Nederlands</Label>
                <Input placeholder="Jaarlijks Verlof" value={formData.name_nl} onChange={(e) => setFormData({ ...formData, name_nl: e.target.value })} />
              </div>
              <div>
                <Label>🇫🇷 Français</Label>
                <Input placeholder="Congé annuel" value={formData.name_fr} onChange={(e) => setFormData({ ...formData, name_fr: e.target.value })} />
              </div>
            </div>
            
            <div>
              <Label>{t('leaveTypes.defaultDays')}</Label>
              <Input type="number" min="0" max="365" value={formData.default_limit_days} onChange={(e) => setFormData({ ...formData, default_limit_days: e.target.value })} />
            </div>
            
            <div>
              <Label>{t('leaveTypes.color')}</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {presetColors.map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setFormData({ ...formData, color })}
                    className={cn("w-7 h-7 rounded-full border-2 transition-all", formData.color === color ? "border-slate-900 scale-110" : "border-transparent hover:scale-105")}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <Label>{t('leaveTypes.requiresDocument')}</Label>
              <Switch checked={formData.requires_document} onCheckedChange={(v) => setFormData({ ...formData, requires_document: v })} />
            </div>
            
            <div className="flex items-center justify-between">
              <Label>{t('leaveTypes.isActive')}</Label>
              <Switch checked={formData.is_active} onCheckedChange={(v) => setFormData({ ...formData, is_active: v })} />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>{t('leaveTypes.adminOnly')}</Label>
                <p className="text-xs text-slate-400">{t('leaveTypes.adminOnlyDesc')}</p>
              </div>
              <Switch checked={formData.admin_only} onCheckedChange={(v) => setFormData({ ...formData, admin_only: v })} />
            </div>
          </div>
          
          <DialogFooter className="flex-shrink-0 border-t pt-4">
            <Button variant="outline" onClick={() => setCreateDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(v) => !v && setEditDialog({ open: false, leaveType: null })}>
        <DialogContent className="max-w-md max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>{t('leaveTypes.editTitle')}</DialogTitle>
            <DialogDescription>{t('leaveTypes.code')}: {editDialog.leaveType?.code}</DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 py-2 pr-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>🇹🇷 Türkçe *</Label>
                <Input placeholder="Yıllık İzin" value={formData.name_tr} onChange={(e) => setFormData({ ...formData, name_tr: e.target.value })} />
              </div>
              <div>
                <Label>🇬🇧 English</Label>
                <Input placeholder="Annual Leave" value={formData.name_en} onChange={(e) => setFormData({ ...formData, name_en: e.target.value })} />
              </div>
              <div>
                <Label>🇳🇱 Nederlands</Label>
                <Input placeholder="Jaarlijks Verlof" value={formData.name_nl} onChange={(e) => setFormData({ ...formData, name_nl: e.target.value })} />
              </div>
              <div>
                <Label>🇫🇷 Français</Label>
                <Input placeholder="Congé annuel" value={formData.name_fr} onChange={(e) => setFormData({ ...formData, name_fr: e.target.value })} />
              </div>
            </div>
            
            <div>
              <Label>{t('leaveTypes.defaultDays')}</Label>
              <Input type="number" min="0" max="365" value={formData.default_limit_days} onChange={(e) => setFormData({ ...formData, default_limit_days: e.target.value })} />
              {editDialog.leaveType && parseInt(formData.default_limit_days) !== editDialog.leaveType.default_limit_days && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <Sparkles className="h-3 w-3" />
                  {t('leaveTypes.daysCountChanged') || 'Gün sayısı değişti! Kaydettiğinizde tüm kullanıcılara yansıtma seçeneği sunulacak.'}
                </p>
              )}
            </div>
            
            <div>
              <Label>{t('leaveTypes.color')}</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {presetColors.map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setFormData({ ...formData, color })}
                    className={cn("w-7 h-7 rounded-full border-2 transition-all", formData.color === color ? "border-slate-900 scale-110" : "border-transparent hover:scale-105")}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <Label>{t('leaveTypes.requiresDocument')}</Label>
              <Switch checked={formData.requires_document} onCheckedChange={(v) => setFormData({ ...formData, requires_document: v })} />
            </div>
            
            <div className="flex items-center justify-between">
              <Label>{t('leaveTypes.isActive')}</Label>
              <Switch checked={formData.is_active} onCheckedChange={(v) => setFormData({ ...formData, is_active: v })} />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
              <div>
                <Label className="text-purple-900">{t('leaveTypes.adminOnly')}</Label>
                <p className="text-xs text-purple-600 mt-1">{t('leaveTypes.adminOnlyDesc')}</p>
              </div>
              <Switch checked={formData.admin_only} onCheckedChange={(v) => setFormData({ ...formData, admin_only: v })} />
            </div>
          </div>
          
          <DialogFooter className="flex-shrink-0 border-t pt-4">
            <Button variant="outline" onClick={() => setEditDialog({ open: false, leaveType: null })}>{t('common.cancel')}</Button>
            <Button onClick={checkAndUpdate} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply to All Users Dialog */}
      <AlertDialog open={applyToAllDialog.open} onOpenChange={(v) => !v && setApplyToAllDialog({ open: false, leaveType: null, oldDays: 0, newDays: 0 })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-500" />
              Gün Sayısı Değişti
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                <strong>{applyToAllDialog.leaveType?.name_i18n?.tr}</strong> izin türünün varsayılan gün sayısı{' '}
                <Badge variant="outline" className="mx-1">{applyToAllDialog.oldDays} gün</Badge>
                <ArrowRight className="inline h-4 w-4 mx-1" />
                <Badge className="mx-1 bg-emerald-500">{applyToAllDialog.newDays} gün</Badge>
                olarak değiştirilecek.
              </p>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-amber-800 text-sm">
                  <strong>Bu değişikliği mevcut tüm kullanıcılara da uygulamak ister misiniz?</strong>
                </p>
                <p className="text-amber-700 text-xs mt-1">
                  "Tüm Kullanıcılara Uygula" seçerseniz, bu izin türüne sahip tüm kullanıcıların hakkedişi güncellenecek.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <Button variant="outline" onClick={() => handleUpdate(false)} disabled={saving}>
              Sadece Türü Güncelle
            </Button>
            <Button onClick={() => handleUpdate(true)} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Tüm Kullanıcılara Uygula
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(v) => !v && setDeleteDialog({ open: false, leaveType: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>İzin Türünü Sil</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteDialog.leaveType && getLocalizedName(deleteDialog.leaveType)}</strong> izin türünü silmek istediğinize emin misiniz?
              Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Users Dialog - Shows users with this leave type */}
      <Dialog open={usersDialog.open} onOpenChange={(v) => !v && setUsersDialog({ open: false, leaveType: null, users: [], loading: false })}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: usersDialog.leaveType?.color || '#6B7280' }} />
              {usersDialog.leaveType && getLocalizedName(usersDialog.leaveType)} - Kullanıcılar
            </DialogTitle>
            <DialogDescription>
              Bu izin türüne sahip kullanıcıları görüntüleyin ve bakiyelerini yönetin
            </DialogDescription>
          </DialogHeader>
          
          {usersDialog.loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <>
              {/* Stats */}
              <div className="flex gap-4 mb-4">
                <div className="flex-1 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-600">Toplam Bakiye</p>
                  <p className="text-xl font-bold text-blue-800">{usersDialog.totalBalances || 0}</p>
                </div>
                <div className="flex-1 p-3 bg-amber-50 rounded-lg">
                  <p className="text-sm text-amber-600">Toplam Talep</p>
                  <p className="text-xl font-bold text-amber-800">{usersDialog.totalRequests || 0}</p>
                </div>
              </div>
              
              {/* Users List */}
              <div className="flex-1 overflow-auto">
                {usersDialog.users.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                    <Users className="h-12 w-12 mx-auto mb-2 opacity-30" />
                    <p>Bu izin türünü kullanan kimse yok</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Kullanıcı</TableHead>
                        <TableHead className="text-center">Yıl</TableHead>
                        <TableHead className="text-center">Hakkediş</TableHead>
                        <TableHead className="text-center">Kullanılan</TableHead>
                        <TableHead className="text-center">Kalan</TableHead>
                        <TableHead className="text-center">Talepler</TableHead>
                        <TableHead className="text-right">İşlem</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {usersDialog.users.map((item) => {
                        const remaining = calculateRemaining(item.balance);
                        return (
                          <TableRow key={item.user.id + (item.balance?.id || '')}>
                            <TableCell>
                              <div className="font-medium">{item.user.full_name}</div>
                              <div className="text-xs text-slate-400">{item.user.email}</div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline">{item.balance?.year || '-'}</Badge>
                            </TableCell>
                            <TableCell className="text-center font-semibold">
                              {item.balance?.entitled_days ?? '-'}
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="text-red-600">{item.balance?.used_days || 0}</span>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge className={cn(
                                "font-mono",
                                remaining > 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                              )}>
                                {remaining} gün
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              {item.request_count > 0 ? (
                                <Badge className="bg-amber-100 text-amber-700">{item.request_count}</Badge>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {item.has_balance && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => removeUserBalance(item.user.id, usersDialog.leaveType.code, item.user.full_name, item.balance?.id)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </div>
            </>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setUsersDialog({ open: false, leaveType: null, users: [], loading: false })}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicates Dialog */}
      <Dialog open={duplicatesDialog.open} onOpenChange={(v) => !v && setDuplicatesDialog({ open: false, loading: false, duplicates: [] })}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5 text-amber-500" />
              Mükerrer Bakiye Kontrolü
            </DialogTitle>
            <DialogDescription>
              Aynı kullanıcı + izin türü + yıl kombinasyonuna sahip mükerrer bakiyeleri görüntüleyin ve silin
            </DialogDescription>
          </DialogHeader>
          
          {duplicatesDialog.loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : duplicatesDialog.duplicates.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="h-16 w-16 mx-auto text-emerald-500 mb-4" />
              <p className="text-lg font-medium text-slate-700 dark:text-slate-200">Harika! Mükerrer kayıt bulunamadı.</p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Tüm bakiyeler benzersiz durumda.</p>
            </div>
          ) : (
            <div className="flex-1 overflow-auto">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                <p className="text-amber-800 font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  {duplicatesDialog.duplicates.length} adet mükerrer grup bulundu
                </p>
                <p className="text-amber-700 text-sm mt-1">
                  Her gruptan yalnızca bir tanesini tutun, diğerlerini silin.
                </p>
              </div>
              
              <div className="space-y-4">
                {duplicatesDialog.duplicates.map((group, gIdx) => (
                  <Card key={gIdx} className="border-amber-200">
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <span className="font-medium">{group.user_name}</span>
                        <Badge variant="outline">{group.leave_type_name}</Badge>
                        <Badge>{group.year}</Badge>
                        <Badge variant="destructive">{group.items.length} kayıt</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="py-2">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>ID</TableHead>
                            <TableHead>Hakkediş</TableHead>
                            <TableHead>Kullanılan</TableHead>
                            <TableHead>Oluşturulma</TableHead>
                            <TableHead className="text-right">İşlem</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {group.items.map((item, iIdx) => (
                            <TableRow key={item.id}>
                              <TableCell className="font-mono text-xs">{item.id.slice(0, 8)}...</TableCell>
                              <TableCell>{item.entitled_days} gün</TableCell>
                              <TableCell>{item.used_days || 0} gün</TableCell>
                              <TableCell className="text-xs">{item.created_at?.slice(0, 10) || '-'}</TableCell>
                              <TableCell className="text-right">
                                {iIdx > 0 ? (
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => deleteDuplicateBalance(item.id, group.user_name, group.leave_type_name)}
                                  >
                                    <Trash2 className="h-4 w-4 mr-1" />
                                    Sil
                                  </Button>
                                ) : (
                                  <Badge className="bg-emerald-100 text-emerald-700">Tut</Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicatesDialog({ open: false, loading: false, duplicates: [] })}>
              Kapat
            </Button>
            {duplicatesDialog.duplicates.length > 0 && (
              <Button variant="outline" onClick={fetchDuplicates}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Yenile
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Orphan Balances Dialog */}
      <Dialog open={orphansDialog.open} onOpenChange={(v) => !v && setOrphansDialog({ open: false, loading: false, orphans: [] })}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Yetim Bakiye Kontrolü
            </DialogTitle>
            <DialogDescription>
              Kullanıcının mevcut firmasına ait olmayan bakiyeler (örn: firma değişikliği sonrası kalan eski bakiyeler)
            </DialogDescription>
          </DialogHeader>
          
          {orphansDialog.loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
            </div>
          ) : orphansDialog.orphans.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 mx-auto text-emerald-500 mb-3" />
              <p className="text-lg font-medium text-slate-700 dark:text-slate-200">Harika! Yetim bakiye bulunamadı.</p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Tüm bakiyeler kullanıcıların mevcut firmalarıyla eşleşiyor.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between bg-orange-50 p-3 rounded-lg border border-orange-200">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-600" />
                  <span className="text-sm font-medium text-orange-800">
                    {orphansDialog.orphans.length} adet yetim bakiye bulundu
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={cleanupAllOrphans}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Tümünü Temizle
                </Button>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kullanıcı</TableHead>
                    <TableHead>İzin Türü</TableHead>
                    <TableHead>Yıl</TableHead>
                    <TableHead>Bakiye</TableHead>
                    <TableHead className="text-right">İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orphansDialog.orphans.map((orphan, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{orphan.user_name}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{orphan.user_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>{orphan.leave_type_name}</TableCell>
                      <TableCell>{orphan.year}</TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {orphan.entitled_days} gün hak, {orphan.used_days} gün kullanılmış
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => deleteOrphanBalance(orphan.balance_id, orphan.user_name, orphan.leave_type_name)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrphansDialog({ open: false, loading: false, orphans: [] })}>
              Kapat
            </Button>
            {orphansDialog.orphans.length > 0 && (
              <Button variant="outline" onClick={fetchOrphans}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Yenile
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeaveTypesManagement;
